/**
 * The start wizard (FD §3.2, B7): six friendly steps from nothing to a saved plan open in the editor.
 *   1 name · 2 land (blank or screenshot) · 3 themes · 4 animals · 5 style · 6 pick one of three layouts.
 * Back keeps every choice, Skip uses sensible defaults, and too many animals warns but carries on
 * (the extras are written into the plan's Unplaced tray, doc.unplaced, before it is saved).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as api from '../../api';
import themesData from '../../../../data/themes.json';
import type { PlanDoc, Species, Theme, ThemeId } from '../../../shared/types';
import { OverlayCalibrator, downscaleImage, type CalibrationResult } from '../editor/overlay/OverlayCalibrator';
import { errorText, toast } from '../shell/dialogs';
import { defaultPlanName } from '../shell/format';
import { Link, navigate } from '../shell/router';
import { generateLayouts, stopGenerating, type Layout } from './generate';
import {
  animalsByTheme,
  batchOptions,
  defaultPicks,
  fitOf,
  MAX_THEMES,
  newSeed,
  pickedSpecies,
  sizeMetres,
  surpriseThemes,
  usableLand,
  withUnplaced,
  type Choices,
  type Picks,
} from './logic';
import { clearChoices, loadChoices, STEPS, storeChoices, type Screenshot, type WizardChoices } from './state';
import { AnimalsStep, FitCounter, LandStep, LayoutStep, NameStep, StyleStep, ThemesStep } from './steps';
import './wizard.css';

/** Built-in themes: static data, bundled with the client (there is no themes route). */
const THEMES = themesData as Theme[];

interface Batch {
  key: string;
  seed: number;
  layouts: Layout[] | null;
  error: string;
}

/** The /new page. Takes no props: it creates the plan itself and then navigates to /plan/<id>. */
export function WizardPage() {
  const [c, setC] = useState<WizardChoices>(loadChoices);
  const [species, setSpecies] = useState<Species[] | null>(null);
  const [loadError, setLoadError] = useState('');
  const [shot, setShot] = useState<Screenshot | null>(null);
  const [calibrating, setCalibrating] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [shotError, setShotError] = useState('');
  const [batch, setBatch] = useState<Batch | null>(null);
  const [picked, setPicked] = useState(0);
  const [creating, setCreating] = useState(false);
  const [placeholder] = useState(() => defaultPlanName());
  const batchRef = useRef(0);
  const topRef = useRef<HTMLDivElement>(null);

  const loadSpecies = useCallback(() => {
    setLoadError('');
    api.listSpecies().then(setSpecies, (e: unknown) => setLoadError(errorText(e)));
  }, []);
  useEffect(loadSpecies, [loadSpecies]);
  useEffect(() => storeChoices(c), [c]);
  useEffect(() => () => stopGenerating(), []);
  useEffect(() => {
    const url = shot?.url;
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [shot?.url]);

  // What the choices add up to.
  const size = sizeMetres(c.sizeId);
  const boundary = c.start === 'screenshot' ? (shot?.calibration?.boundary ?? null) : null;
  const usable = usableLand(size, boundary);
  const groups = useMemo(() => animalsByTheme(c.themes, THEMES, species ?? []), [c.themes, species]);
  const pickList = useMemo(() => pickedSpecies(groups, c.picks), [groups, c.picks]);
  const fit = fitOf(pickList, species ?? [], usable);
  const choices: Choices = { size, boundary, themes: c.themes, picks: pickList, layout: c.layout, water: c.water };
  const choicesKey = JSON.stringify(choices);

  const update = (patch: Partial<WizardChoices>) => setC((prev) => ({ ...prev, ...patch }));

  /** Moves to a step; on the way to the animals (or past them) the default ticks are worked out for new themes or land. */
  const goTo = (step: number, patch: Partial<WizardChoices> = {}) => {
    setC((prev) => {
      const next = { ...prev, ...patch, step };
      if (step >= 3 && species) {
        const g = animalsByTheme(next.themes, THEMES, species);
        const land = usableLand(sizeMetres(next.sizeId), next.start === 'screenshot' ? (shot?.calibration?.boundary ?? null) : null);
        const key = `${next.themes.join(',')}|${Math.round(land)}`;
        if (next.picksFor !== key) {
          const defaults = defaultPicks(g, land);
          next.picks = next.picksTouched ? { ...defaults, ...next.picks } : defaults;
          next.picksFor = key;
        }
      }
      return next;
    });
    topRef.current?.scrollIntoView({ block: 'start' });
  };

  // Step 6: make three layouts whenever the choices changed since the last batch.
  const runBatch = useCallback(
    (seed: number) => {
      if (!species) return;
      const ticket = ++batchRef.current;
      const key = choicesKey;
      setBatch({ key, seed, layouts: null, error: '' });
      setPicked(0);
      generateLayouts(batchOptions(JSON.parse(key) as Choices, seed), species, THEMES).then(
        (layouts) => ticket === batchRef.current && setBatch({ key, seed, layouts, error: '' }),
        (e: unknown) => ticket === batchRef.current && setBatch({ key, seed, layouts: null, error: e instanceof Error && e.message !== 'stopped' ? 'Please try again.' : '' }),
      );
    },
    [species, choicesKey],
  );
  useEffect(() => {
    if (c.step === 5 && species && batch?.key !== choicesKey) runBatch(newSeed());
  }, [c.step, species, choicesKey, batch?.key, runBatch]);

  const toggleTheme = (id: ThemeId) => {
    const on = c.themes.includes(id);
    if (!on && c.themes.length >= MAX_THEMES) return;
    update({ themes: on ? c.themes.filter((t) => t !== id) : [...c.themes, id] });
  };
  const surprise = () => update({ themes: surpriseThemes(THEMES, newSeed()) });

  const setPicks = (fn: (p: Picks) => Picks) => setC((prev) => ({ ...prev, picks: fn(prev.picks), picksTouched: true }));

  const pickFile = async (file: File) => {
    setShotError('');
    setPreparing(true);
    try {
      const blob = await downscaleImage(file);
      setShot({ blob, url: URL.createObjectURL(blob), calibration: null });
      setCalibrating(true);
    } catch (e) {
      setShotError(e instanceof Error ? e.message : "That picture couldn't be opened.");
    } finally {
      setPreparing(false);
    }
  };

  const openLayout = async (i: number) => {
    const layout = batch?.layouts?.[i];
    if (!layout || creating) return;
    setCreating(true);
    const name = c.name.trim() || placeholder;
    const doc: PlanDoc = withUnplaced({ ...layout.doc, name }, layout.unplaced);
    try {
      const meta = await api.createPlan({ name, doc });
      const cal = c.start === 'screenshot' ? shot?.calibration : null;
      if (shot && cal) {
        try {
          const { file } = await api.uploadOverlay(meta.id, shot.blob);
          await api.savePlan(meta.id, { ...doc, overlay: { file, ...cal.overlay } }, meta.rev);
        } catch {
          toast("Your zoo is saved, but the screenshot couldn't be added. You can add it again in the editor.", 'error');
        }
      }
      clearChoices();
      navigate(`/plan/${encodeURIComponent(meta.id)}`, { replace: true });
    } catch (e) {
      toast(errorText(e), 'error');
      setCreating(false);
    }
  };

  if (!species) {
    return (
      <main className="page wz">
        <Link to="/" className="back">← My plans</Link>
        {loadError ? (
          <div className="empty">
            <h2>The animals couldn't be loaded</h2>
            <p>{loadError}</p>
            <button type="button" className="primary" onClick={loadSpecies}>Try again</button>
          </div>
        ) : (
          <p className="muted">Getting the animals ready…</p>
        )}
      </main>
    );
  }

  if (calibrating && shot) {
    return (
      <main className="page wz wide">
        <h1>Line up your screenshot</h1>
        <OverlayCalibrator
          imageUrl={shot.url}
          mapSize={size}
          askBoundary
          onDone={(result: CalibrationResult) => {
            setShot({ ...shot, calibration: result });
            setCalibrating(false);
          }}
          onCancel={() => setCalibrating(false)}
        />
      </main>
    );
  }

  // The bar at the bottom: what Next and Skip do on this step.
  const step = c.step;
  let next: { label: string; disabled?: boolean; run(): void };
  let skip: { label: string; run(): void } | null = null;
  switch (step) {
    case 0:
      next = { label: 'Next', run: () => goTo(1) };
      skip = { label: 'Skip', run: () => goTo(1, { name: '' }) };
      break;
    case 1: {
      const needsShot = c.start === 'screenshot' && !shot?.calibration;
      next = { label: needsShot ? (shot ? 'Line it up first' : 'Choose a screenshot first') : 'Next', disabled: needsShot, run: () => goTo(2) };
      skip = { label: 'Skip: medium blank land', run: () => goTo(2, { start: 'blank', sizeId: 'medium' }) };
      break;
    }
    case 2:
      next = c.themes.length
        ? { label: 'Next', run: () => goTo(3) }
        : { label: 'Surprise me', run: () => goTo(3, { themes: surpriseThemes(THEMES, newSeed()) }) };
      break;
    case 3:
      next = { label: 'Next', run: () => goTo(4) };
      skip = c.picksTouched ? { label: 'Skip: use the suggestions', run: () => goTo(4, { picksTouched: false, picksFor: '' }) } : null;
      break;
    case 4:
      next = { label: 'Next', run: () => goTo(5) };
      skip = c.layout === 'surprise' && c.water === 'surprise' ? null : { label: 'Skip: surprise me', run: () => goTo(5, { layout: 'surprise', water: 'surprise' }) };
      break;
    default:
      next = {
        label: creating ? 'Saving your zoo…' : `Open layout ${picked + 1} in the editor`,
        disabled: creating || !batch?.layouts,
        run: () => void openLayout(picked),
      };
  }

  return (
    <main className="page wz">
      <div ref={topRef} />
      <div className="wz-top">
        <Link to="/" className="back">← My plans</Link>
        <ol className="wz-steps" aria-label="Steps">
          {STEPS.map((s, i) => (
            <li key={s.id} className={i === step ? 'now' : i < step ? 'done' : ''}>
              <button type="button" disabled={i >= step || creating} onClick={() => goTo(i)} aria-current={i === step ? 'step' : undefined}>
                <span className="wz-dot">{i < step ? '✓' : i + 1}</span>
                <span className="wz-step-label">{s.label}</span>
              </button>
            </li>
          ))}
        </ol>
      </div>

      <div className="wz-body">
        {step === 0 && <NameStep name={c.name} placeholder={placeholder} onChange={(name) => update({ name })} onEnter={next.run} />}
        {step === 1 && (
          <LandStep
            start={c.start}
            sizeId={c.sizeId}
            screenshot={shot}
            screenshotError={shotError}
            preparing={preparing}
            onStart={(start) => update({ start })}
            onSize={(sizeId) => {
              update({ sizeId });
              // The lined-up screenshot was measured against the old map size.
              if (shot?.calibration && sizeId !== c.sizeId) setShot({ ...shot, calibration: null });
            }}
            onPickFile={(f) => void pickFile(f)}
            onCalibrate={() => setCalibrating(true)}
          />
        )}
        {step === 2 && <ThemesStep themes={THEMES} species={species} chosen={c.themes} onToggle={toggleTheme} onSurprise={surprise} />}
        {step === 3 && (
          <AnimalsStep
            groups={groups}
            picks={c.picks}
            onToggle={(id) => setPicks((p) => ({ ...p, [id]: { on: !p[id]?.on, count: p[id]?.count ?? species.find((s) => s.id === id)?.groupSize.typical ?? 1 } }))}
            onCount={(id, count) => setPicks((p) => ({ ...p, [id]: { on: true, count } }))}
            onAll={(g, on) =>
              setPicks((p) => {
                const out = { ...p };
                for (const s of g.species) out[s.id] = { on, count: p[s.id]?.count ?? s.groupSize.typical };
                return out;
              })
            }
          />
        )}
        {step === 4 && <StyleStep layout={c.layout} water={c.water} onLayout={(layout) => update({ layout })} onWater={(water) => update({ water })} />}
        {step === 5 && (
          <LayoutStep
            layouts={batch?.layouts ?? null}
            busy={!batch?.layouts && !batch?.error}
            error={batch?.error ?? ''}
            picked={picked}
            wanted={pickList.length}
            onPick={setPicked}
            onOpen={(i) => void openLayout(i)}
            onOthers={() => runBatch(newSeed())}
            onRetry={() => runBatch(batch?.seed ?? newSeed())}
          />
        )}
      </div>

      <div className="wz-bar">
        {step === 3 && <FitCounter fit={fit} picks={pickList} species={species} />}
        <div className="wz-nav">
          {step > 0 ? (
            <button type="button" className="big" disabled={creating} onClick={() => goTo(step - 1)}>← Back</button>
          ) : (
            <span />
          )}
          <span className="grow" />
          {skip && (
            <button type="button" className="quiet" onClick={skip.run}>{skip.label}</button>
          )}
          <button type="button" className="primary big wz-next" disabled={next.disabled} onClick={next.run}>
            {next.label} {step < 5 && '→'}
          </button>
        </div>
      </div>
    </main>
  );
}
